'use client';

import { useEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import {
  resolveContentRefsInHtml,
  injectStorageShim,
  injectContentSizeReporter,
  injectLogForwarder,
  injectTapForwarder,
  injectPinchContainment,
} from '@/lib/content/html-preview';
import { getRuntimeConfig } from '@/lib/runtime-config';
import { perContentOrigin } from '@/lib/domains/sandbox-origin';

/** A captured console / error line forwarded from the widget iframe. */
export type WidgetLogEntry = { level: string; message: string; ts: number };

/**
 * Sandboxed HTML preview.
 *
 * Two render strategies, picked based on whether we have a
 * `contentId` AND `SANDBOX_ORIGIN` is configured for this env:
 *
 *   1. **Sandbox host** (`<iframe src="<SANDBOX_ORIGIN>/widget-sandbox/c/<id>">`)
 *      The outer iframe loads from a SEPARATE eTLD+1 (production:
 *      `robutler.app`; staging/dev: `staging.robutler.app` /
 *      `dev.robutler.app`) so apex cookies cannot reach it regardless
 *      of `Domain=` / `SameSite=` policy. Inner iframe gets
 *      `allow-same-origin` SAFELY because the parent is the sandbox
 *      origin (already isolated from apex). `localStorage` works
 *      (per-iframe bucket on the sandbox host), no apex secrets
 *      exposed. Wire is postMessage: outer signals ready, we send
 *      the HTML body, outer sets srcdoc on inner. Matches the
 *      folder-bundle sandbox pattern
 *      (`app/widget-sandbox/[folderId]/[[...path]]/route.ts`).
 *
 *   2. **Apex srcdoc fallback** (`<iframe srcDoc={html}>`)
 *      Used when `contentId` is missing OR `SANDBOX_ORIGIN` is
 *      unset for this env (local dev on Tailscale Funnel can't
 *      easily provision a second TLS cert — see `useSandboxUrl`
 *      below). NO `allow-same-origin` — srcdoc would inherit apex
 *      origin and grant access to apex cookies / storage. In this
 *      opaque origin `localStorage` / `sessionStorage` throw on
 *      access, so `injectStorageShim` prepends an in-memory fake
 *      ahead of the widget's scripts: storage-using widgets RUN
 *      (losing cross-reload persistence) instead of hard-crashing to
 *      a blank tile. For real persistence the right answer is still
 *      to configure `SANDBOX_ORIGIN` or have the widget use
 *      `host.kv` instead of `localStorage`.
 */
export function HtmlPreviewIframe({
  html,
  title,
  contentId,
  className = 'nowheel h-full w-full border-0 bg-background',
  onContentSize,
  onLog,
  onLoadState,
}: {
  html: string | null;
  title: string;
  /**
   * Persisted-content id. When set + sandbox subdomain is reachable,
   * the iframe loads from the sandbox origin and `localStorage` works
   * (per-iframe bucket on the sandbox origin). When omitted (inline
   * paste, agent stream not yet persisted, test fixtures), falls
   * back to apex-origin srcdoc.
   */
  contentId?: string | null;
  className?: string;
  /**
   * Fires once when the injected size-reporter posts the document's
   * natural extent (see `injectContentSizeReporter`). The caller uses
   * it to auto-fit the canvas tile to the content.
   */
  onContentSize?: (width: number, height: number) => void;
  /**
   * Fires for each console / error line forwarded from the iframe (see
   * `injectLogForwarder`). The caller buffers these per widget so an
   * agent can read them for dev iteration.
   */
  onLog?: (entry: WidgetLogEntry) => void;
  /**
   * Fires with `'ready'` once any instrumentation frame arrives (the
   * injected scripts ran), or `'failed'` if NOTHING arrives within a
   * few seconds — i.e. the document is blank or its scripts were
   * blocked (CSP). Lets the caller record a synthetic load-failure so
   * an agent sees "widget failed to load" even when there's no error.
   */
  onLoadState?: (state: 'ready' | 'failed') => void;
}) {
  const t = useT();
  const [resolved, setResolved] = useState<string | null>(null);
  const sandboxUrl = useSandboxUrl(contentId);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const sawSignalRef = useRef(false);

  // Keep the callbacks in refs so the message listener can mount ONCE
  // and never tear down. The callers pass inline arrows (new identity
  // every render); a deps-array listener would remove+re-add on every
  // render and could MISS the (often late) `robutler-content-size`
  // frame that lands during the gap — which is exactly why auto-fit
  // sometimes never fired. Refs are updated each render below.
  const onContentSizeRef = useRef(onContentSize);
  const onLogRef = useRef(onLog);
  const onLoadStateRef = useRef(onLoadState);
  onContentSizeRef.current = onContentSize;
  onLogRef.current = onLog;
  onLoadStateRef.current = onLoadState;

  // Instrumentation channel: the injected reporter + forwarder post
  // `robutler-content-size` / `robutler-widget-log` frames. Scope to our
  // own iframe's window. Works for the srcdoc path directly and for the
  // sandbox-host path once the shell relays these frames upward.
  // Mounted once (refs above) so no frame is dropped to listener churn.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (ev: MessageEvent) => {
      if (ev.source !== iframeRef.current?.contentWindow) return;
      const d = ev.data;
      if (!d || typeof d !== 'object') return;
      const isSize = d.source === 'robutler-content-size';
      const isLog = d.source === 'robutler-widget-log';
      if ((isSize || isLog) && !sawSignalRef.current) {
        sawSignalRef.current = true;
        onLoadStateRef.current?.('ready');
      }
      if (isSize && onContentSizeRef.current) {
        const w = Number(d.width);
        const h = Number(d.height);
        if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
          onContentSizeRef.current(w, h);
        }
      } else if (isLog && onLogRef.current) {
        onLogRef.current({
          level: typeof d.level === 'string' ? d.level : 'log',
          message: typeof d.message === 'string' ? d.message : String(d.message ?? ''),
          ts: typeof d.ts === 'number' ? d.ts : Date.now(),
        });
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Load-failure heuristic (srcdoc path): the injected size-reporter
  // posts on load for ANY document whose scripts run — so if NO frame
  // arrives within the window, the scripts were blocked (CSP) or the
  // document is blank. Reset + re-arm whenever the body changes.
  useEffect(() => {
    if (sandboxUrl) return; // relay path arms its own readiness elsewhere
    if (html == null) return;
    sawSignalRef.current = false;
    const t = setTimeout(() => {
      if (!sawSignalRef.current) onLoadStateRef.current?.('failed');
    }, 6000);
    return () => clearTimeout(t);
  }, [html, resolved, sandboxUrl]);

  // Resolve content-refs in the HTML (turns `content://...` and
  // friends into absolute /api/content/<id> URLs the iframe can
  // fetch from its sandbox origin via CORS).
  useEffect(() => {
    if (html == null) {
      setResolved(null);
      return;
    }
    let cancelled = false;
    void resolveContentRefsInHtml(html).then((doc) => {
      if (!cancelled) setResolved(doc);
    });
    return () => {
      cancelled = true;
    };
  }, [html]);

  // Sandbox postMessage wire: when the outer iframe signals
  // `sandbox-single-content-ready` we reply with the HTML body so it
  // can populate the inner srcdoc. The handler is idempotent — the
  // outer can ask again after navigation and we'll re-send.
  useEffect(() => {
    if (!sandboxUrl || !contentId) return;
    if (typeof window === 'undefined') return;
    const handler = (ev: MessageEvent) => {
      // Only honour messages from our own iframe.
      if (ev.source !== iframeRef.current?.contentWindow) return;
      const data = ev.data;
      if (
        !data ||
        data.method !== 'ui/notifications/sandbox-single-content-ready' ||
        data.params?.contentId !== contentId
      ) {
        return;
      }
      const body = resolved ?? html ?? '';
      iframeRef.current?.contentWindow?.postMessage(
        {
          jsonrpc: '2.0',
          method: 'ui/notifications/sandbox-resource-ready',
          params: { contentId, html: body },
        },
        // The sandbox subdomain origin — pin it instead of `'*'` to
        // avoid leaking the body to other windows that might pretend
        // to be the iframe.
        new URL(sandboxUrl).origin,
      );
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [sandboxUrl, contentId, resolved, html]);

  // Re-send the body whenever the HTML changes (live edit) — the
  // outer iframe stays mounted across edits, so we push the new
  // body in without a full iframe reload. The outer doesn't need a
  // second `-ready` ping; it's listening for `-resource-ready`
  // continuously.
  useEffect(() => {
    if (!sandboxUrl || !contentId) return;
    if (typeof window === 'undefined') return;
    if (resolved == null && html == null) return;
    const body = resolved ?? html ?? '';
    iframeRef.current?.contentWindow?.postMessage(
      {
        jsonrpc: '2.0',
        method: 'ui/notifications/sandbox-resource-ready',
        params: { contentId, html: body },
      },
      new URL(sandboxUrl).origin,
    );
  }, [resolved, html, sandboxUrl, contentId]);

  if (html == null && !sandboxUrl) {
    return (
      <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
        {t('htmlPreview.loading')}
      </div>
    );
  }

  if (sandboxUrl && contentId) {
    return (
      <iframe
        ref={iframeRef}
        src={sandboxUrl}
        title={title}
        // No `sandbox=` attribute on the OUTER iframe — the outer is
        // already cross-origin (sandbox subdomain) which is the
        // primary isolation. The INNER iframe (inside the sandbox
        // shell) carries the real sandbox attributes including
        // `allow-same-origin` so its own `localStorage` works.
        className={className}
      />
    );
  }

  // Fallback: apex-origin srcdoc. No allow-same-origin (see header
  // comment), so the document runs in an opaque origin where
  // `window.localStorage` / `sessionStorage` THROW on access. We inject
  // an in-memory shim ahead of the widget's own scripts so a widget
  // that touches storage degrades gracefully (runs, but loses
  // persistence across reloads) instead of hard-crashing to a blank
  // tile. The shim is a per-document fake — it grants no apex access,
  // so the isolation guarantee is unchanged.
  //
  // CRITICAL: render the iframe only once `resolved` is ready and give
  // it a content-derived `key`, so React MOUNTS A FRESH iframe element
  // per document version rather than mutating a live iframe's `srcdoc`
  // in place. Inside react-flow's transformed/scaled canvas, mutating
  // srcdoc on an already-loaded iframe (the `null → html → resolved`
  // sequence, or a live edit) leaves the document BLANK in Chromium —
  // a known srcdoc compositing quirk. A clean keyed mount (matching a
  // hand-created iframe) paints reliably.
  if (resolved == null) {
    return (
      <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
        {t('htmlPreview.loading')}
      </div>
    );
  }
  // CRITICAL prop ORDER + `key`:
  //  • `sandbox` MUST precede `srcDoc`. React applies props in key/JSX
  //    order at creation (before inserting into the DOM). If `srcDoc`
  //    is applied first, the document briefly loads UN-sandboxed and
  //    then re-evaluates when `sandbox` lands — inside react-flow's
  //    transformed canvas that race leaves the iframe BLANK in Chromium.
  //    Sandbox-first mounts a clean, already-sandboxed document (matching
  //    a hand-built `document.createElement('iframe')` that sets the
  //    attribute before assigning srcdoc) and paints reliably.
  //  • `key` (content length) makes each document version a FRESH
  //    element, so we never mutate a live iframe's srcdoc in place
  //    (which also blanks once the doc is large enough).
  return (
    <iframe
      key={`${contentId ?? 'inline'}:${resolved.length}`}
      ref={iframeRef}
      title={title}
      sandbox="allow-scripts allow-forms"
      className={className}
      // Compositing fix for srcdoc-iframe-inside-CSS-transform: inside
      // react-flow's transformed/scaled canvas, a srcdoc iframe can
      // paint BLANK (white) in Chromium — the content layer is never
      // composited under the ancestor transform (intermittent, worse as
      // the document grows). Promoting the layer at mount does NOT help,
      // and toggling at `load` time is too early (the blank paint hasn't
      // committed). After a beat we INVALIDATE the layer with a
      // `none → translateZ(0)` toggle, which forces a recomposite and
      // paints reliably (matches the manual fix that worked live).
      // Identity transform — no visual offset.
      onLoad={(e) => {
        const el = e.currentTarget;
        window.setTimeout(() => {
          if (!el.isConnected) return;
          el.style.transform = 'none';
          requestAnimationFrame(() => {
            if (el.isConnected) el.style.transform = 'translateZ(0)';
          });
        }, 150);
      }}
      srcDoc={injectPinchContainment(injectTapForwarder(injectLogForwarder(injectContentSizeReporter(injectStorageShim(resolved)))))}
    />
  );
}

/**
 * Build the sandbox-host URL for a single content row.
 *
 * The sandbox origin is supplied by the SSR-injected runtime config
 * (`lib/runtime-config.ts`). Cloud envs set `SANDBOX_ORIGIN` per
 * environment (production: `https://robutler.app`; dev:
 * `https://dev.robutler.app`; staging: `https://staging.robutler.app`).
 * Local dev leaves it unset → returns `null` → caller falls back to
 * the apex-srcdoc renderer.
 *
 * Note: we cannot use `NEXT_PUBLIC_*` envs here — those are baked
 * into the build output and would lock one value into every
 * deployment. See `lib/runtime-config.ts` header.
 */
function useSandboxUrl(contentId?: string | null): string | null {
  if (!contentId) return null;
  const { sandboxOrigin, sandboxPerContent } = getRuntimeConfig();
  if (!sandboxOrigin) return null;
  // ADR-0039: when the family is on, this app gets an origin of its own and the
  // browser partitions its storage from every other app. `perContentOrigin`
  // answers null when the family is off or the id is not a safe DNS label, and
  // the path form below is what it falls back to -- so both shapes stay live
  // and the switch is the only thing that decides.
  const own = perContentOrigin(sandboxOrigin, contentId, sandboxPerContent);
  if (own) return `${own}/widget-sandbox/c/${encodeURIComponent(contentId)}`;
  return `${sandboxOrigin}/widget-sandbox/c/${encodeURIComponent(contentId)}`;
}
